import { Grid, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useContext, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import { find, isUndefined, map } from "lodash";
import HumanResourceFields from "./HumanResourceFields";
import EquipmentResourceFields from "./EquipmentResourceFields";
import SubContractorResourceFields from "./SubContractorResourceFields";
import CurrentUserContext from "@/Contexts/CurrentUser";
import { CompanyResourceResourceTypeEnum, CompanyResource, CompanyResourceCreate, ResourceMetaCreate, CreateCompanyResourceDocument, UpdateCompanyResourceDocument, GetResourceCategoriesDocument, FuelTypeEnum, ResourceMetaStatusTypeEnum } from "@/graphql/generated/graphql";
import { titleizeCamelCase } from "@/utils/helperFunctions";
import { displayNotification } from "@/utils/notifications";
import { isValidEmail } from "@/utils/userInformation";
import OverwatchModal from "@/components/OverwatchModal";
import type { ResourceMetaChangeHandler } from "./types";

dayjs.extend(utc);

interface CreateCompanyResourceModalProps {
  resourceType?: CompanyResourceResourceTypeEnum;
  equipmentType?: string | undefined;
  handleClose: () => void;
  selectedResource?: CompanyResource | undefined;
}

const CreateCompanyResourceModal: React.FC<CreateCompanyResourceModalProps> = ({
  resourceType,
  equipmentType,
  handleClose,
  selectedResource,
}) => {
  const { currentUser } = useContext(CurrentUserContext);
  const [resetKey, setResetKey] = useState<number>(0);

  const getInitialValues = (): CompanyResourceCreate => {
    const meta = selectedResource?.resourceMeta;

    // Process the meta mapping logic
    const initialMeta: ResourceMetaCreate = meta
      ? ((({ id, __typename, attachments, imageUrls, resourceCategory, ...rest }) => ({
        ...rest,
        resourceCategoryId: resourceCategory?.id,
      }))(meta) as ResourceMetaCreate)
      : ({ equipmentType: equipmentType ?? undefined } as ResourceMetaCreate);

    return {
      name: selectedResource?.name ?? "",
      resourceType: resourceType ?? CompanyResourceResourceTypeEnum.Human,
      resourceMeta: initialMeta,
    };
  };

  const [companyResource, setCompanyResource] = useState<CompanyResourceCreate>(getInitialValues);

  const [inviteSubContractor, setInviteSubContractor] = useState<boolean>(false);

  const [createResource] = useMutation(CreateCompanyResourceDocument);
  const [updateResource] = useMutation(UpdateCompanyResourceDocument);

  const { data: categories } = useQuery(GetResourceCategoriesDocument, {
    skip: !currentUser?.defaultCompany?.id,
    variables: { companyId: currentUser?.defaultCompany?.id as string },
  });

  const disableSubmit = () => {
    return companyResource.name === "";
  };

  const resourceCategories = categories?.getResourceCategories || [];

  const isVehicleCategory = () => {
    const vehicleCategory = find((resourceCategories), category => category.name === "Vehicle") || false;

    return resourceType === CompanyResourceResourceTypeEnum.Equipment && vehicleCategory && vehicleCategory.id === companyResource.resourceMeta?.resourceCategoryId
  };

  const handleSubmit = () => {
    const resourceData = {
      ...companyResource,
      ...(inviteSubContractor && { inviteSubContractor: inviteSubContractor })
    }

    if (selectedResource) {
      updateResource({
        variables: { companyResource: { id: selectedResource.id, ...resourceData } },
        refetchQueries: ["getCompanyResources"],
        onCompleted: () => {
          displayNotification({ type: "success", text: "Company Resource Created Successfully." })
          handleClose();
        },
        onError: () => displayNotification({ type: "error", text: "Unable to create company resource." })
      });
    } else {
      createResource({
        variables: { companyResource: resourceData },
        refetchQueries: ["getCompanyResources"],
        onCompleted: () => {
          displayNotification({ type: "success", text: "Company Resource Created Successfully." })
          handleClose();
        },
        onError: () => displayNotification({ type: "error", text: "Unable to create company resource." })
      });
    }
  };

  const handleMetaUpdate: ResourceMetaChangeHandler = (key, value) => {
    if (key === "resourceCategoryId") {
      const canResetVehicleData = find((resourceCategories), category => category.name === "Vehicle")?.id === value || false
      setCompanyResource((prev) => ({
        ...prev,
        resourceMeta: {
          [key]: value,
          equipmentType: equipmentType || undefined,
          ...(canResetVehicleData && { fuelType: FuelTypeEnum.Diesel, status: ResourceMetaStatusTypeEnum.Available })
        },
      }));
    }

    setCompanyResource((prev) => ({
      ...prev,
      resourceMeta: {
        ...prev.resourceMeta,
        [key]: value,
      },
    }));
  };

  const canSendInvite =
    companyResource.resourceType === CompanyResourceResourceTypeEnum.SubContractor &&
    !!companyResource.resourceMeta?.pointOfContact &&
    !!companyResource.resourceMeta?.email &&
    isValidEmail(companyResource.resourceMeta?.email);

  return (
    <OverwatchModal
      title={`${selectedResource ? "Editing" : "Creating"} Company Resource`}
      primaryButtonText={`${selectedResource ? "Update" : "Create"}`}
      size="sm"
      primaryButtonAction={handleSubmit}
      disablePrimary={disableSubmit()}
      handleClose={handleClose}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
      tertiaryButtonText="Reset"
      tertiaryButtonAction={() => {
        setResetKey(prev => prev + 1); // Incrementing this forces children to re-mount
        setInviteSubContractor(false);
        setCompanyResource(getInitialValues());
      }}
    >
      <Grid container padding={2}>
        <ToggleButtonGroup
          exclusive
          value={resourceType ? resourceType : undefined}
          onChange={(_, newSelection) => setCompanyResource((prev) => ({ ...prev, resourceType: newSelection || "" }))}
          aria-label="company-resource-type"
          color="primary"
          disabled={!!resourceType}
          sx={{ mb: 2 }}
        >
          {map(Object.values(CompanyResourceResourceTypeEnum), (type) => (
            <ToggleButton
              size="small"
              key={type}
              value={type}
            >
              {titleizeCamelCase(type)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
      <Grid container size={12} p={2}>
        <TextField
          label="Resource Name"
          variant="outlined"
          value={companyResource.name}
          fullWidth
          onChange={(e) =>
            setCompanyResource((prev) => ({
              ...prev,
              name: e.target.value,
            }))
          }
        />
      </Grid>
      {resourceType === CompanyResourceResourceTypeEnum.Human && (
        <HumanResourceFields
          key={resetKey}
          resourceMeta={companyResource.resourceMeta}
          onChange={handleMetaUpdate}
        />
      )}

      {resourceType === CompanyResourceResourceTypeEnum.Equipment && (
        <EquipmentResourceFields
          key={resetKey}
          isEditing={!isUndefined(selectedResource)}
          resourceMeta={companyResource.resourceMeta}
          equipmentType={equipmentType}
          onChange={handleMetaUpdate}
          isVehicle={isVehicleCategory()}
          resourceCategories={resourceCategories}
          attachments={selectedResource?.resourceMeta?.attachments}
        />
      )}

      {resourceType === CompanyResourceResourceTypeEnum.SubContractor && (
        <SubContractorResourceFields
          key={resetKey}
          isEditing={!isUndefined(selectedResource)}
          resourceMeta={companyResource.resourceMeta}
          onChange={handleMetaUpdate}
          canSendInvite={canSendInvite}
          inviteSubContractor={inviteSubContractor}
          setInviteSubContractor={setInviteSubContractor}
        />
      )}
    </OverwatchModal>
  );
};

export default CreateCompanyResourceModal;
